import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NbTokenLocalStorage } from '@nebular/auth';
import { NbToastRef, NbToastrService } from '@nebular/theme';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { BloodBankModuleData } from '../pages/blood-bank/backend/interface/blood-bank';

/** Seconds the user is warned before an idle session is ended. */
export const IDLE_WARNING_SECONDS = 60;

const DEFAULT_SESSION_MINUTES = 1440;
const FALLBACK_IDLE_SECONDS = 3600;

/**
 * Ends a signed-in session after a period without user activity.
 *
 * The idle clock itself is shared by every open tab of the app (ng-idle's LocalStorageExpiry, see
 * AppModule), so activity in any tab keeps all of them signed in; a tab that merely looks idle can no
 * longer clear the shared localStorage under a tab that is in use.
 */
@Injectable({ providedIn: 'root' })
export class SessionIdleService implements OnDestroy {

  private readonly destroy$ = new Subject<void>();
  private appliedIdleSeconds: number;
  private warning: NbToastRef = null;
  private started = false;

  constructor(private idle: Idle,
              private router: Router,
              private tokenStorage: NbTokenLocalStorage,
              private toastr: NbToastrService,
              private postService: BloodBankModuleData) { }

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.applyIdleSeconds(this.configuredIdleSeconds());
    this.idle.setTimeout(IDLE_WARNING_SECONDS);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onIdleStart.pipe(takeUntil(this.destroy$)).subscribe(() => this.showWarning());
    this.idle.onIdleEnd.pipe(takeUntil(this.destroy$)).subscribe(() => this.closeWarning());
    this.idle.onTimeout.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.closeWarning();
      this.logout();
    });

    // The timeout setting only exists once the user has signed in (it is read from the account), and
    // the clock stops after a timeout: pick both up without waiting for a page reload.
    this.idle.onInterrupt.pipe(takeUntil(this.destroy$)).subscribe(() => this.syncWithSession());
    this.router.events.pipe(filter(event => event instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(() => this.syncWithSession());

    this.idle.watch();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Re-reads the account's timeout and restarts the clock if the user signed in again after a timeout. */
  syncWithSession(): void {
    const wanted = this.configuredIdleSeconds();
    if (wanted !== this.appliedIdleSeconds) {
      this.applyIdleSeconds(wanted);
      if (this.idle.isRunning()) {
        this.idle.watch();
      }
    }
    if (!this.idle.isRunning() && this.isSignedIn()) {
      this.idle.watch();
    }
  }

  private configuredIdleSeconds(): number {
    const minutes = parseInt(localStorage.getItem('sessionTimeOutSettings'), 10);
    const seconds = (isNaN(minutes) ? DEFAULT_SESSION_MINUTES : minutes) * 60;
    return seconds || FALLBACK_IDLE_SECONDS;
  }

  private applyIdleSeconds(seconds: number): void {
    this.appliedIdleSeconds = seconds;
    this.idle.setIdle(seconds);
  }

  private isSignedIn(): boolean {
    try {
      return !!this.tokenStorage.get()?.getValue();
    } catch {
      return false;
    }
  }

  private showWarning(): void {
    if (!this.isSignedIn()) {
      return;
    }
    this.closeWarning();
    this.warning = this.toastr.warning(
      `You will be signed out in about ${IDLE_WARNING_SECONDS} seconds because of inactivity. ` +
      'Move the mouse or press a key to stay signed in.',
      'Session timeout',
      { duration: 0, destroyByClick: true, preventDuplicates: false, status: 'warning' });
  }

  private closeWarning(): void {
    if (this.warning) {
      this.warning.close();
      this.warning = null;
    }
  }

  private logout(): void {
    if (this.router.url !== '/auth/login' && this.router.url !== '/auth/logout') {
      // Another tab (or the 401 handler) may already have ended the session and cleared the shared
      // storage: there is nothing left to sign out here.
      if (!this.isSignedIn()) {
        return;
      }

      const postData = {
        sessionId: localStorage.getItem('sessionId'),
        tenantId: localStorage.getItem('tenantId'),
        branchId: localStorage.getItem('branchId'),
        LogOutType: 'System'
      };

      this.postService.searchList('auth/sign-out-webApp', postData).pipe(takeUntil(this.destroy$)).subscribe(() => {
        localStorage.clear();
        sessionStorage.clear();
        this.toastr.warning('Your session has expired. Please login again', 'Session expired',
          { duration: 15000, destroyByClick: true, status: 'warning' });
        this.router.navigateByUrl('/auth/login');
      }, () => {
        // Unchanged: the sign-out call failed, so the session stays and the next 401 ends it.
      });
    }
  }
}
